import { useCallback, useEffect, useState } from 'react';
import type { AcademyStudentMessageRecipientSnapshot } from '@court-sync/shared';
import {
  ArrowLeft,
  Calendar,
  Mail,
  MessageSquare,
  Smartphone,
  User,
  Users,
} from 'lucide-react';
import { Badge, Button, Drawer, Skeleton } from '@/components/ui';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { useFormatDate } from '@/hooks/useFormatDate';
import { academyStudentService } from '@/services/academyStudentService';
import { cn } from '@/lib/utils';

interface MessageLogRow {
  id: string;
  subject: string;
  body: string;
  channels: { inApp: boolean; email: boolean; sms: boolean };
  targetKind: 'student_ids' | 'session';
  targetStudentCount: number;
  targetSummary: Record<string, unknown> | null;
  sentInApp: number;
  sentEmail: number;
  sentSms: number;
  skipped: number;
  sentAt: string;
  senderName?: string;
}

type MessageLogDetail = MessageLogRow & {
  recipients: AcademyStudentMessageRecipientSnapshot[];
};

function parseRecipientsFromSummary(
  targetSummary: Record<string, unknown> | null
): AcademyStudentMessageRecipientSnapshot[] {
  const raw = targetSummary?.recipients;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is AcademyStudentMessageRecipientSnapshot =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as AcademyStudentMessageRecipientSnapshot).studentId === 'string' &&
      typeof (item as AcademyStudentMessageRecipientSnapshot).name === 'string'
  );
}

function recipientKindLabel(kind: AcademyStudentMessageRecipientSnapshot['recipientKind']): string {
  switch (kind) {
    case 'self_student':
      return 'Own account';
    case 'guardian':
      return 'Guardian';
    case 'invite_only':
      return 'Invite email';
    default:
      return 'Student';
  }
}

function formatTargetSummary(row: MessageLogRow): string {
  if (row.targetKind === 'session' && row.targetSummary) {
    const name = String(row.targetSummary.lessonName ?? 'Class');
    const date = String(row.targetSummary.sessionDate ?? '');
    const start = String(row.targetSummary.startTime ?? '').slice(0, 5);
    const end = String(row.targetSummary.endTime ?? '').slice(0, 5);
    return `${name} · ${date} ${start}–${end}`;
  }
  return `${row.targetStudentCount} student${row.targetStudentCount !== 1 ? 's' : ''}`;
}

function formatRecipientsPreview(recipients: AcademyStudentMessageRecipientSnapshot[]): string {
  if (recipients.length === 0) return '';
  if (recipients.length === 1) return recipients[0].name;
  if (recipients.length === 2) return `${recipients[0].name}, ${recipients[1].name}`;
  return `${recipients[0].name}, ${recipients[1].name} + ${recipients.length - 2} more`;
}

function ChannelBadges({ channels }: { channels: MessageLogRow['channels'] }) {
  const items: Array<{ key: string; label: string; icon: typeof Mail }> = [];
  if (channels.inApp) items.push({ key: 'inApp', label: 'In-app', icon: MessageSquare });
  if (channels.email) items.push({ key: 'email', label: 'Email', icon: Mail });
  if (channels.sms) items.push({ key: 'sms', label: 'SMS', icon: Smartphone });

  if (items.length === 0) {
    return <span className="text-xs text-muted-foreground">No channels</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(({ key, label, icon: Icon }) => (
        <Badge key={key} variant="outline" className="gap-1 text-xs font-normal">
          <Icon className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
          {label}
        </Badge>
      ))}
    </div>
  );
}

function DeliverySummary({ row, compact = false }: { row: MessageLogRow; compact?: boolean }) {
  const totalDelivered = row.sentInApp + row.sentEmail + row.sentSms;
  const stats = [
    { label: 'In-app', count: row.sentInApp, enabled: row.channels.inApp },
    { label: 'Email', count: row.sentEmail, enabled: row.channels.email },
    { label: 'SMS', count: row.sentSms, enabled: row.channels.sms },
  ].filter((s) => s.enabled);

  if (stats.length === 0) {
    return <span className="text-xs text-muted-foreground">No channels selected</span>;
  }

  return (
    <div className={cn('flex flex-wrap gap-2', compact ? 'gap-1.5' : 'gap-3')}>
      {stats.map(({ label, count }) => (
        <span
          key={label}
          className={cn(
            'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
            count > 0
              ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
              : 'bg-muted text-muted-foreground'
          )}
        >
          {count} {label.toLowerCase()}
        </span>
      ))}
      {row.skipped > 0 && (
        <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
          {row.skipped} skipped
        </span>
      )}
      {!compact && totalDelivered === 0 && row.skipped === 0 && (
        <span className="text-xs text-muted-foreground">Nothing delivered</span>
      )}
    </div>
  );
}

function RecipientsList({
  recipients,
  loading,
  targetLabel,
  targetKind,
}: {
  recipients: AcademyStudentMessageRecipientSnapshot[];
  loading?: boolean;
  targetLabel?: string;
  targetKind?: MessageLogRow['targetKind'];
}) {
  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (recipients.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {targetLabel ? `Target: ${targetLabel}` : 'No recipient details available.'}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {targetLabel && (
        <p className="text-xs text-muted-foreground">
          {targetLabel}
          {targetKind === 'session' && recipients.length > 0
            ? ` · ${recipients.length} student${recipients.length !== 1 ? 's' : ''}`
            : ''}
        </p>
      )}
      <ul className="max-h-56 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-background">
        {recipients.map((recipient) => (
          <li
            key={recipient.studentId}
            className="flex items-start justify-between gap-3 px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{recipient.name}</p>
              {recipient.contact && (
                <p className="truncate text-xs text-muted-foreground">{recipient.contact}</p>
              )}
            </div>
            <Badge variant="outline" className="shrink-0 text-xs font-normal">
              {recipientKindLabel(recipient.recipientKind)}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MessageHistorySkeleton() {
  return (
    <ul className="space-y-3" aria-hidden>
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="rounded-lg border border-border p-4">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="mt-2 h-3 w-1/2" />
          <Skeleton className="mt-3 h-6 w-full" />
        </li>
      ))}
    </ul>
  );
}

export interface StudentMessageHistoryDrawerProps {
  academyId: string;
  open: boolean;
  onClose: () => void;
}

export function StudentMessageHistoryDrawer({
  academyId,
  open,
  onClose,
}: StudentMessageHistoryDrawerProps) {
  const { formatDateTime } = useFormatDate();
  const [rows, setRows] = useState<MessageLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detail, setDetail] = useState<MessageLogDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    if (!academyId) return;
    setLoading(true);
    try {
      const res = await academyStudentService.getStudentMessageHistory(academyId, page, 20);
      if (res.success && res.data) {
        setRows(res.data as MessageLogRow[]);
        setTotalPages(res.pagination?.totalPages ?? 1);
      }
    } finally {
      setLoading(false);
    }
  }, [academyId, page]);

  const openDetail = useCallback(
    async (row: MessageLogRow) => {
      setDetail({ ...row, recipients: parseRecipientsFromSummary(row.targetSummary) });
      setDetailLoading(true);
      try {
        const res = await academyStudentService.getStudentMessageDetail(academyId, row.id);
        if (res.success && res.data) {
          setDetail(res.data as MessageLogDetail);
        }
      } finally {
        setDetailLoading(false);
      }
    },
    [academyId]
  );

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setDetailLoading(false);
      setPage(1);
    }
  }, [open]);

  const handleClose = () => {
    setDetail(null);
    onClose();
  };

  return (
    <Drawer
      isOpen={open}
      onClose={handleClose}
      title={detail ? detail.subject : 'Message history'}
      description={
        detail
          ? `Sent ${formatDateTime(detail.sentAt)}`
          : 'Review past notifications sent to students from this academy.'
      }
      width="lg"
    >
      {detail ? (
        <div className="space-y-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 gap-1.5 text-muted-foreground"
            onClick={() => setDetail(null)}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Back to list
          </Button>

          <dl className="grid gap-4 rounded-lg border border-border bg-muted/30 p-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Sent by
              </dt>
              <dd className="mt-1 flex items-center gap-1.5 font-medium text-foreground">
                <User className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
                {detail.senderName ?? 'Staff'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Sent at
              </dt>
              <dd className="mt-1 font-medium text-foreground">{formatDateTime(detail.sentAt)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Channels
              </dt>
              <dd>
                <ChannelBadges channels={detail.channels} />
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Delivery
              </dt>
              <dd>
                <DeliverySummary row={detail} />
              </dd>
            </div>
          </dl>

          <section aria-labelledby="message-body-heading">
            <h3
              id="message-body-heading"
              className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              Message
            </h3>
            <div className="rounded-lg border border-border bg-background p-4">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {detail.body}
              </p>
            </div>
          </section>

          <section aria-labelledby="message-recipients-heading">
            <h3
              id="message-recipients-heading"
              className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              {detail.targetKind === 'session' ? (
                <Calendar className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <Users className="h-3.5 w-3.5" aria-hidden />
              )}
              Recipients
            </h3>
            <RecipientsList
              recipients={detail.recipients}
              loading={detailLoading}
              targetLabel={
                detail.targetKind === 'session' ? formatTargetSummary(detail) : undefined
              }
              targetKind={detail.targetKind}
            />
          </section>
        </div>
      ) : (
        <div className="space-y-4">
          {loading && <MessageHistorySkeleton />}

          {!loading && rows.length === 0 && (
            <DashboardEmptyStateCard className="m-0 border-dashed shadow-none">
              <DashboardEmptyStateIcon icon={MessageSquare} />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No messages yet
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                When you send notifications from Student Management, they will appear here for audit
                and reference.
              </p>
            </DashboardEmptyStateCard>
          )}

          {!loading && rows.length > 0 && (
            <ul className="space-y-3">
              {rows.map((row) => {
                const previewRecipients = parseRecipientsFromSummary(row.targetSummary);
                const recipientPreview = formatRecipientsPreview(previewRecipients);

                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="group w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => void openDetail(row)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-medium text-foreground group-hover:text-primary">
                          {row.subject}
                        </p>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatDateTime(row.sentAt)}
                        </span>
                      </div>

                      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <User className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                        {row.senderName ?? 'Staff'}
                      </p>

                      <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                        {row.targetKind === 'session' ? (
                          <Calendar className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                        ) : (
                          <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                        )}
                        <span>
                          {recipientPreview || formatTargetSummary(row)}
                        </span>
                      </p>

                      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <ChannelBadges channels={row.channels} />
                        <DeliverySummary row={row} compact />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border pt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
